/**
 * Create Or Edit Shape Tool
 * 
 * Create a new shape or edit an existing shape on a slide.
 * Uses paragraphOperations[] for text editing (not paragraphs[]).
 * 
 * Primary: Office.js TypeScript implementation
 * Fallback: Python backend COM service at /com/createShape
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import {
    CreateOrEditShapeParamsSchema,
    type CreateOrEditShapeParams,
} from "./createOrEditShape.schema";
import { getOrCreatedocumentId } from "../../../../../services/documentIdManager";
import { backendClient } from "../../../../../services/communication/backendClient";
import { getContextResolver } from "../../../../core/contextResolver";
import { resolveImagePosition, getAspectRatioFromImage } from "../../../../../utils/imageAspectRatio";
import { applyShapeMetadata } from "../../../../../services/metadataService";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const SYSTEM_PROMPT_FALLBACK = `### createOrEditShape
Create a new shape or edit an existing shape on a slide.

**When to use:**
- Creating text boxes, rectangles, ellipses, triangles, arrows, lines
- Editing text content in existing shapes
- Changing shape styling (colors, fonts, borders)
- Inserting images into shapes
 
**Text precision note:** Be very precise with run styling. Many shapes contain mixed styling (bold/italic emphasis, specific word highlights, superscripts/subscripts). Preserve or explicitly reapply run styling when editing text.

**Text editing uses paragraphOperations (not paragraphs):**
\`\`\`json
{
  "slideId": 1,
  "shapeId": 123,
  "textContent": {
    "paragraphOperations": [
      {
        "operation": "edit",
        "index": 0,
        "paragraph": {
          "setText": "New Title",
          "textFormat": [{ "text": "New Title", "fontSize": 14, "bold": true }]
        }
      }
    ]
  }
}
\`\`\`

**Creating shapes:**
\`\`\`json
{
  "slideId": 1,
  "shapeType": "textbox",
  "position": { "left": 50, "top": 100, "width": 200, "height": 50 },
  "textContent": {
    "paragraphOperations": [
      {
        "operation": "insert",
        "index": 0,
        "paragraph": {
          "setText": "Hello World",
          "textFormat": [{ "text": "Hello World", "fontSize": 18 }]
        }
      }
    ]
  }
}
\`\`\``;

const DEFAULT_TEXT_MARGIN_PERCENT_VERTICAL = 12;
const DEFAULT_TEXT_MARGIN_PERCENT_HORIZONTAL = 6;

const roundToTwoDecimals = (value: number): number =>
    Math.round(value * 100) / 100;

const roundPosition = (position?: {
    left: number;
    top: number;
    width: number;
    height: number;
}): { left: number; top: number; width: number; height: number } | undefined => {
    if (!position) return undefined;
    return {
        left: roundToTwoDecimals(position.left),
        top: roundToTwoDecimals(position.top),
        width: roundToTwoDecimals(position.width),
        height: roundToTwoDecimals(position.height),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// RESPONSE TYPES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Create or edit shape result
 */
export interface CreateOrEditShapeResult {
    success: boolean;
    slideId: number;
    shapeId: number;
    operationType: "create" | "edit";
    position?: {
        left: number;
        top: number;
        width: number;
        height: number;
    };
    warnings: string[];
    errors: string[];
    executionMethod?: "officejs" | "com"; // Track which method was used
}

// ═══════════════════════════════════════════════════════════════════════════
// OFFICE.JS SHAPE TYPE MAPPING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Map our shape types to PowerPoint.GeometricShapeType.
 * NOTE: Keep this lazy to avoid PowerPoint undefined at module load.
 */
function getShapeTypeMap(): Record<string, PowerPoint.GeometricShapeType> {
    if (typeof PowerPoint === 'undefined') {
        throw new Error('PowerPoint namespace is not available (Office.js not ready)');
    }
    return {
        "rectangle": PowerPoint.GeometricShapeType.rectangle,
        "ellipse": PowerPoint.GeometricShapeType.ellipse,
        "triangle": PowerPoint.GeometricShapeType.triangle,
        "arrow": PowerPoint.GeometricShapeType.rightArrow,
        // line is handled via addLine(), textbox via addTextBox()
    };
}

// ═══════════════════════════════════════════════════════════════════════════
// PARAMETER PREPROCESSING
// ═══════════════════════════════════════════════════════════════════════════

/**
 * LLMs sometimes double-serialize JSON objects into strings.
 * This function recursively parses stringified JSON fields.
 */
// Fields that should be arrays (LLM sometimes sends single object instead)
const ARRAY_FIELDS = new Set(['paragraphOperations', 'addEntries', 'runFormatting']);

/**
 * Attempt to repair malformed JSON with unescaped quotes inside string values.
 * This handles the case where LLM outputs: "setText": "Hello "World" there"
 * Which should be: "setText": "Hello \"World\" there"
 */
function repairJsonString(jsonString: string): string {
    let result = '';
    let inString = false;
    let i = 0;

    while (i < jsonString.length) {
        const char = jsonString[i];
        const prevChar = i > 0 ? jsonString[i - 1] : '';

        if (char === '"' && prevChar !== '\\') {
            if (!inString) {
                // Starting a string
                inString = true;
                result += char;
            } else {
                // In a string, hit a quote - is it end of string or unescaped content?
                // End of string is followed by: , } ] : or whitespace then those
                const rest = jsonString.substring(i + 1);
                const trimmedRest = rest.trimStart();
                const nextMeaningful = trimmedRest[0];

                if (nextMeaningful === ',' || nextMeaningful === '}' ||
                    nextMeaningful === ']' || nextMeaningful === ':' ||
                    nextMeaningful === undefined) {
                    // End of string
                    inString = false;
                    result += char;
                } else {
                    // Unescaped quote inside string - escape it
                    result += '\\"';
                }
            }
        } else {
            result += char;
        }
        i++;
    }

    return result;
}

function preprocessParameters(params: unknown): unknown {
    if (typeof params !== 'object' || params === null) {
        return params;
    }

    // Handle arrays - process each element
    if (Array.isArray(params)) {
        return params.map(item => preprocessParameters(item));
    }

    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
        if (typeof value === 'string' && value.trim().startsWith('{')) {
            // Attempt to parse stringified JSON
            try {
                let parsed = JSON.parse(value);
                // Wrap in array if this field expects an array
                if (ARRAY_FIELDS.has(key) && !Array.isArray(parsed)) {
                    parsed = [parsed];
                    console.log(`[preprocessParameters] Wrapped parsed JSON in array for "${key}"`);
                }
                result[key] = preprocessParameters(parsed);
                console.log(`[preprocessParameters] Parsed stringified JSON for "${key}"`);
            } catch {
                // JSON parse failed - try repairing unescaped quotes
                try {
                    const repaired = repairJsonString(value);
                    let parsed = JSON.parse(repaired);
                    if (ARRAY_FIELDS.has(key) && !Array.isArray(parsed)) {
                        parsed = [parsed];
                    }
                    result[key] = preprocessParameters(parsed);
                } catch {
                    // Still failed, keep as string
                    result[key] = value;
                }
            }
        } else if (typeof value === 'object' && value !== null) {
            // Check if this should be an array but LLM sent a single object
            if (ARRAY_FIELDS.has(key) && !Array.isArray(value)) {
                console.log(`[preprocessParameters] Wrapped object in array for "${key}"`);
                result[key] = [preprocessParameters(value)];
            } else {
                // Recursively process nested objects/arrays
                result[key] = preprocessParameters(value);
            }
        } else {
            result[key] = value;
        }
    }
    return result;
}

// ═══════════════════════════════════════════════════════════════════════════
// CREATE OR EDIT SHAPE TOOL
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tool for creating or editing shapes on slides.
 * 
 * Features:
 * - Create new shapes (textbox, rectangle, ellipse, line, triangle, arrow)
 * - Edit existing shapes by ID
 * - Full styling control (fill, outline, shadow)
 * - Text editing with paragraphOperations[]
 * - Spacing consequences tracking
 * - Metadata support in edit mode
 */
export class CreateOrEditShapeTool extends ToolBase<CreateOrEditShapeParams, CreateOrEditShapeResult> {
    public readonly name = "createOrEditShape";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = SYSTEM_PROMPT_FALLBACK;
    
    // Streaming operation fields
    fieldsToCheck = [
        { name: 'slideId', type: 'number' as const, list_status: false, order: 1 },
        { name: 'operationType', type: 'string' as const, list_status: false, order: 2 },
        { name: 'shapeId', type: 'number' as const, list_status: false, order: 3 },
        { name: 'shapeType', type: 'string' as const, list_status: false, order: 4 },
    ];
    
    listsToCheck = [];

    constructor() {
        super();
        this.description = new ToolDescription(
            "createOrEditShape",
            `Create a new shape or edit an existing shape on a slide.

**Purpose:** Create or modify shapes with full styling and text control.

**Operation Types:**
- \`create\`: Create new shape (requires shapeType)
- \`edit\`: Modify existing shape (requires shapeId)

**Shape Types (for create):**
- textbox, rectangle, ellipse, line, triangle, arrow

**Features:**
- Full styling: fill (solid/image), outline, shadow
- Text with paragraphOperations[] (insert/remove/edit)
- Spacing consequences tracking
- Metadata in edit mode

**Image Fill (CRITICAL - Use Asset IDs):**
- When using image fills, the \`imageSource\` parameter MUST be an asset ID from imageSearchAgent
- Asset IDs are in format: \`logo_xxxxx\`, \`wikimedia_xxxxx\`, \`color_xxxxx\`
- **DO NOT use filenames** like "microsoft_logo.png" - use the exact asset ID (e.g., "logo_omxqz4pl")
- These asset IDs are provided in context when orchestrator passes image search results
- Example: \`{ "type": "image", "imageSource": "logo_omxqz4pl", "fillMode": "center" }\`

**Text Operations (edit mode):**
\`\`\`json
{
  "textContent": {
    "paragraphOperations": [
      { "operation": "insert", "index": 0, "paragraph": { "setText": "New text", "textFormat": [{ "text": "New text", "fontSize": 14 }] } },
      { "operation": "edit", "index": 1, "paragraph": { "replaceText": { "find": "old", "replace": "new" } } },
      { "operation": "remove", "index": 2 }
    ]
  }
}
\`\`\`

**Spacing Consequences:**
Track layout impact: \`spacingConsequences: "Adding paragraph, text box grows ~25pt"\`

**Example (Create):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "create",
  "shapeType": "textbox",
  "position": { "left": 100, "top": 100, "width": 200, "height": 50 },
  "fill": { "type": "solid", "color": "#FFFFFF" },
  "textContent": {
    "paragraphOperations": [
      { "operation": "insert", "index": 0, "paragraph": { "setText": "Title", "textFormat": [{ "text": "Title", "bold": true }] } }
    ]
  }
}
\`\`\`

**Example (Edit with setText - REQUIRES runs to preserve formatting):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 5,
  "textContent": {
    "paragraphOperations": [
      { "operation": "edit", "index": 0, "paragraph": { "setText": "New text overwrites all", "textFormat": [{ "text": "New text overwrites all" }] } }
    ]
  }
}
\`\`\`

**Example (Edit with replaceText - PRESERVES formatting, use for cloned slides):**
\`\`\`json
{
  "slideId": 1,
  "operationType": "edit",
  "shapeId": 5,
  "textContent": {
    "paragraphOperations": [
      { "operation": "edit", "index": 0, "paragraph": { "replaceText": { "find": "Company A", "replace": "Company B" } } }
    ]
  }
}
\`\`\`

**Example (Create with Image Fill - Use Asset ID):**
\`\`\`json
{
  "slideId": 15,
  "operationType": "create",
  "shapeType": "rectangle",
  "position": { "left": 70, "top": 155, "width": 70, "height": 30 },
  "fill": { "type": "image", "imageSource": "logo_omxqz4pl", "fillMode": "center" },
  "outline": { "null": true }
}
\`\`\`
**Note:** \`imageSource\` must be the exact asset ID from imageSearchAgent (e.g., "logo_omxqz4pl"), NOT a filename like "microsoft_logo.png"`,
            CreateOrEditShapeParamsSchema
        );
    }

    public async execute(parameters: unknown): Promise<CreateOrEditShapeResult> {
        console.log(`[CreateOrEditShapeTool] ========== EXECUTE CALLED ==========`);
        console.log(`[CreateOrEditShapeTool] Raw parameters received:`, JSON.stringify(parameters, null, 2));
        
        // Preprocess to handle LLM double-serialization of JSON objects
        const preprocessed = preprocessParameters(parameters);
        console.log(`[CreateOrEditShapeTool] After preprocessing:`, JSON.stringify(preprocessed, null, 2));
        
        const parseResult = safeParseToolParams(CreateOrEditShapeParamsSchema, preprocessed);
        if (!parseResult.success) {
            console.error(`[CreateOrEditShapeTool] Schema validation FAILED:`, parseResult.error);
            throw new Error(`Invalid parameters: ${parseResult.error}`);
        }

        const params = parseResult.data;
        console.log(`[CreateOrEditShapeTool] Parsed params:`, JSON.stringify(params, null, 2));
        console.log(`[CreateOrEditShapeTool] params.textContent:`, params.textContent);
        console.log(`[CreateOrEditShapeTool] params.textContent?.paragraphOperations:`, params.textContent?.paragraphOperations);
        
        if (params.textContent?.paragraphOperations) {
            console.log(`[CreateOrEditShapeTool] Paragraph operations detail:`);
            params.textContent.paragraphOperations.forEach((op, i) => {
                console.log(`  [${i}] operation=${op.operation}, index=${op.index}`);
                console.log(`      paragraph.setText=${op.paragraph?.setText}`);
                console.log(`      paragraph.textFormat=${op.paragraph?.textFormat?.length ?? 0}`);
                console.log(`      paragraph.paragraphAlignment=${op.paragraph?.paragraphAlignment}`);
            });
        }
        
        const isCreate = params.operationType === "create";
        
        console.log(`[CreateOrEditShapeTool] ${isCreate ? 'Creating' : 'Editing'} shape on slide ${params.slideId}`);
        if (isCreate) {
            console.log(`  Type: ${params.shapeType}`);
        } else {
            console.log(`  Shape ID: ${params.shapeId}`);
        }

        // Check if this requires COM (e.g., image fills - Office.js doesn't support them)
        if (params.fill?.type === 'image') {
            console.log(`[CreateOrEditShapeTool] Image fill detected - using COM directly (Office.js doesn't support image fills)`);
            try {
                const comResult = await this.executeWithCOM(params);
                return await this.finalizeResult(params, comResult);
            } catch (comError) {
                const errorMsg = comError instanceof Error ? comError.message : String(comError);
                console.error(`[CreateOrEditShapeTool] COM failed for image fill:`, errorMsg);
                return {
                    success: false,
                    slideId: params.slideId,
                    shapeId: params.shapeId ?? 0,
                    operationType: params.operationType,
                    warnings: [],
                    errors: [`Image fill error: ${errorMsg}`],
                };
            }
        }

        // Run-level formatting requires COM
        const hasTextFormat = params.textContent?.paragraphOperations?.some(
            op => op.paragraph?.textFormat && op.paragraph.textFormat.length > 0
        );
        if (hasTextFormat) {
            console.log(`[CreateOrEditShapeTool] textFormat detected - using COM directly (Office.js cannot apply run formatting)`);
            return await this.executeWithCOM(params);
        }

        // Check if this has replaceText operations - use COM to preserve formatting
        const replaceTextOps = params.textContent?.paragraphOperations?.filter(
            op => op.operation === 'edit' && op.paragraph?.replaceText
        ) || [];

        if (replaceTextOps.length > 0 && params.operationType === 'edit' && params.shapeId) {
            try {
                const comResult = await this.executeReplaceTextWithCOM(params, replaceTextOps);
                return await this.finalizeResult(params, comResult);
            } catch (comError) {
                const errorMsg = comError instanceof Error ? comError.message : String(comError);
                console.error(`[CreateOrEditShapeTool] COM replaceText failed:`, errorMsg);
                // Fall through to Office.js as backup (will lose formatting but still work)
                console.warn(`[CreateOrEditShapeTool] Falling back to Office.js (formatting will be lost)`);
            }
        }

        // Try Office.js first (preferred), fall back to COM if it fails
        try {
            console.log(`[CreateOrEditShapeTool] Attempting Office.js implementation...`);
            const officeResult = await this.executeWithOfficeJS(params);
            console.log(`[CreateOrEditShapeTool] Office.js succeeded:`, officeResult);
            return await this.finalizeResult(params, officeResult);
        } catch (officeError) {
            const errorMsg = officeError instanceof Error ? officeError.message : String(officeError);
            console.warn(`[CreateOrEditShapeTool] Office.js failed (${errorMsg}), falling back to COM...`);
            
            // Fall back to COM implementation
            try {
                const comResult = await this.executeWithCOM(params);
                comResult.warnings.push(`Office.js failed: ${errorMsg}. Used COM fallback.`);
                return await this.finalizeResult(params, comResult);
            } catch (comError) {
                const comErrorMsg = comError instanceof Error ? comError.message : String(comError);
                console.error(`[CreateOrEditShapeTool] Both Office.js and COM failed`);
                return {
                    success: false,
                    slideId: params.slideId,
                    shapeId: params.shapeId ?? 0,
                    operationType: params.operationType,
                    warnings: [],
                    errors: [
                        `Office.js error: ${errorMsg}`,
                        `COM error: ${comErrorMsg}`
                    ],
                };
            }
        }
    }

    private async finalizeResult(
        params: CreateOrEditShapeParams,
        result: CreateOrEditShapeResult
    ): Promise<CreateOrEditShapeResult> {
        if (!result.success) {
            return result;
        }

        const shapeId = String(result.shapeId ?? params.shapeId ?? "");
        if (shapeId) {
            await applyShapeMetadata({
                slideId: params.slideId,
                shapeId,
                metadata: params.metadata,
            });
        }

        return result;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // OFFICE.JS IMPLEMENTATION (PRIMARY)
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Execute shape creation/editing using Office.js PowerPoint API
     */
    private async executeWithOfficeJS(params: CreateOrEditShapeParams): Promise<CreateOrEditShapeResult> {
        const warnings: string[] = [];
        
        console.log(`[CreateOrEditShapeTool:OfficeJS] Starting ${params.operationType} operation...`);
        
        return await PowerPoint.run(async (context) => {
            // Get the target slide (1-based index to 0-based)
            console.log(`[CreateOrEditShapeTool:OfficeJS] Getting slide at index ${params.slideId - 1} (1-based: ${params.slideId})`);
            const slide = context.presentation.slides.getItemAt(params.slideId - 1);
            slide.load('id');
            await context.sync();
            console.log(`[CreateOrEditShapeTool:OfficeJS] Got slide with Office.js ID: ${slide.id}`);
            
            let shape: PowerPoint.Shape;
            let shapeId: number = 0;
            
            if (params.operationType === "create") {
                // CREATE: Add a new shape
                console.log(`[CreateOrEditShapeTool:OfficeJS] Creating new ${params.shapeType} shape...`);
                shape = await this.createShapeOfficeJS(slide, params, warnings);
                console.log(`[CreateOrEditShapeTool:OfficeJS] Shape created successfully`);
            } else {
                // EDIT: Get existing shape by ID
                console.log(`[CreateOrEditShapeTool:OfficeJS] Looking for shape with ID: ${params.shapeId}`);
                const shapes = slide.shapes;
                shapes.load('items');
                await context.sync();
                console.log(`[CreateOrEditShapeTool:OfficeJS] Found ${shapes.items.length} shapes on slide`);
                
                // Load all shape IDs to find our target
                for (const s of shapes.items) {
                    s.load('id,name');
                }
                await context.sync();
                
                // Log all shape IDs for debugging
                const shapeIdList = shapes.items.map(s => `${s.id} (${s.name || 'unnamed'})`).join(', ');
                console.log(`[CreateOrEditShapeTool:OfficeJS] Available shape IDs: [${shapeIdList}]`);
                
                const foundShape = shapes.items.find(s => {
                    // Compare as strings - Office.js IDs vary by platform
                    const sId = s.id;
                    return sId === String(params.shapeId) || 
                           sId === params.shapeId?.toString() ||
                           parseInt(sId, 10) === params.shapeId;
                });
                
                if (!foundShape) {
                    console.error(`[CreateOrEditShapeTool:OfficeJS] Shape ${params.shapeId} NOT FOUND! Available IDs: [${shapeIdList}]`);
                    throw new Error(`Shape with ID ${params.shapeId} not found on slide ${params.slideId}. Available: [${shapeIdList}]`);
                }
                
                console.log(`[CreateOrEditShapeTool:OfficeJS] Found shape: ID=${foundShape.id}, name=${foundShape.name}`);
                shape = foundShape;
                
                console.log(`[CreateOrEditShapeTool:OfficeJS] Calling editShapeOfficeJS...`);
                await this.editShapeOfficeJS(shape, params, context, warnings);
                console.log(`[CreateOrEditShapeTool:OfficeJS] editShapeOfficeJS completed`);
            }
            
            // Load final shape properties
            console.log(`[CreateOrEditShapeTool:OfficeJS] Loading final shape properties...`);
            shape.load(['id', 'left', 'top', 'width', 'height']);
            await context.sync();
            
            // Parse shape ID (Office.js returns string IDs)
            shapeId = parseInt(shape.id, 10) || 0;
            console.log(`[CreateOrEditShapeTool:OfficeJS] SUCCESS! Shape ID: ${shapeId}, Position: (${shape.left}, ${shape.top}, ${shape.width}x${shape.height})`);
            
            return {
                success: true,
                slideId: params.slideId,
                shapeId,
                operationType: params.operationType,
                position: roundPosition({
                    left: shape.left,
                    top: shape.top,
                    width: shape.width,
                    height: shape.height,
                }),
                warnings,
                errors: [],
                executionMethod: "officejs" as const,
            };
        });
    }

    /**
     * Create a new shape using Office.js
     */
    private async createShapeOfficeJS(
        slide: PowerPoint.Slide,
        params: CreateOrEditShapeParams,
        warnings: string[]
    ): Promise<PowerPoint.Shape> {
        const left = params.position?.left ?? 100;
        const top = params.position?.top ?? 100;
        const width = params.position?.width ?? 200;
        const height = params.position?.height ?? 100;
        
        console.log(`[CreateOrEditShapeTool:createShape] Creating shape: type=${params.shapeType}, pos=(${left}, ${top}, ${width}x${height})`);
        console.log(`[CreateOrEditShapeTool:createShape] Full params:`, JSON.stringify(params, null, 2));
        
        let shape: PowerPoint.Shape;
        
        // Create shape based on type
        switch (params.shapeType) {
            case "textbox":
                // Office.js doesn't have a direct addTextBox, use rectangle with no fill
                console.log(`[CreateOrEditShapeTool:createShape] Adding textbox (as rectangle)...`);
                shape = slide.shapes.addGeometricShape(
                    PowerPoint.GeometricShapeType.rectangle,
                    { left, top, width, height }
                );
                console.log(`[CreateOrEditShapeTool:createShape] Textbox shape added`);
                // We'll remove the fill/outline after sync to make it a textbox
                break;
                
            case "line":
                // addLine takes position with left, top, width, height
                // Note: Office.js may ignore height=0 during creation, so we set it explicitly after
                console.log(`[CreateOrEditShapeTool:createShape] Adding line with target height=${height}...`);
                shape = slide.shapes.addLine(
                    PowerPoint.ConnectorType.straight,
                    { left, top, width, height }
                );
                // Office.js ignores height=0 during addLine, so explicitly set dimensions after creation
                // This is required for horizontal lines (height=0) and vertical lines (width=0)
                shape.left = left;
                shape.top = top;
                shape.width = width;
                shape.height = height;
                console.log(`[CreateOrEditShapeTool:createShape] Line added, dimensions set to ${width}x${height}`);
                break;
                
            case "rectangle":
            case "ellipse":
            case "triangle":
            case "arrow":
                const shapeTypeMap = getShapeTypeMap();
                const geometricType = shapeTypeMap[params.shapeType];
                if (!geometricType) {
                    throw new Error(`Unsupported shape type: ${params.shapeType}`);
                }
                console.log(`[CreateOrEditShapeTool:createShape] Adding ${params.shapeType} (geometricType=${geometricType})...`);
                shape = slide.shapes.addGeometricShape(
                    geometricType,
                    { left, top, width, height }
                );
                console.log(`[CreateOrEditShapeTool:createShape] ${params.shapeType} added`);
                break;
                
            default:
                throw new Error(`Unknown shape type: ${params.shapeType}`);
        }
        
        // Apply styling and text
        console.log(`[CreateOrEditShapeTool:createShape] Now applying styling and text...`);
        await this.applyShapeStylingOfficeJS(shape, params, slide.context, warnings);
        console.log(`[CreateOrEditShapeTool:createShape] Styling and text applied`);
        
        return shape;
    }

    /**
     * Edit an existing shape using Office.js
     */
    private async editShapeOfficeJS(
        shape: PowerPoint.Shape,
        params: CreateOrEditShapeParams,
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        console.log(`[CreateOrEditShapeTool:editShape] Starting edit...`);
        
        // Update position if provided
        if (params.position) {
            console.log(`[CreateOrEditShapeTool:editShape] Updating position:`, params.position);
            if (params.position.left !== undefined) shape.left = params.position.left;
            if (params.position.top !== undefined) shape.top = params.position.top;
            if (params.position.width !== undefined) shape.width = params.position.width;
            if (params.position.height !== undefined) shape.height = params.position.height;
            console.log(`[CreateOrEditShapeTool:editShape] Position updated`);
        }
        
        // Apply styling updates
        console.log(`[CreateOrEditShapeTool:editShape] Applying styling...`);
        await this.applyShapeStylingOfficeJS(shape, params, context, warnings);
        console.log(`[CreateOrEditShapeTool:editShape] Styling applied`);
    }

    /**
     * Apply styling (fill, outline, text) to a shape
     */
    private async applyShapeStylingOfficeJS(
        shape: PowerPoint.Shape,
        params: CreateOrEditShapeParams,
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        console.log(`[CreateOrEditShapeTool:applyStyling] Starting styling application...`);
        console.log(`[CreateOrEditShapeTool:applyStyling] Has fill: ${!!params.fill}, Has outline: ${!!params.outline}, Has textContent: ${!!params.textContent?.paragraphOperations}`);
        
        // Apply fill
        if (params.fill) {
            console.log(`[CreateOrEditShapeTool:applyStyling] Applying fill:`, JSON.stringify(params.fill));
            try {
                shape.fill.load('type');
                await context.sync();
                
                if (params.fill.type === 'solid') {
                    // Check if this should be "no fill":
                    // - explicit null flag
                    // - transparency of 1 (100% transparent)
                    // - textbox shape type (typically no fill)
                    const isNoFill = params.fill.null || 
                                     params.fill.transparency === 1 ||
                                     (params.shapeType === 'textbox' && !params.fill.color);
                    
                    console.log(`[CreateOrEditShapeTool:applyStyling] Fill decision: isNoFill=${isNoFill}, null=${params.fill.null}, transparency=${params.fill.transparency}, shapeType=${params.shapeType}`);
                    
                    if (isNoFill) {
                        // No fill - clear it and DON'T set transparency (would cause InvalidArgument)
                        console.log(`[CreateOrEditShapeTool:applyStyling] Clearing fill (no fill)`);
                        shape.fill.clear();
                    } else if (params.fill.color) {
                        // Apply solid color
                        console.log(`[CreateOrEditShapeTool:applyStyling] Setting solid color: ${params.fill.color}`);
                        shape.fill.setSolidColor(params.fill.color);
                        // Only set transparency if there's a fill AND transparency < 1
                        if (params.fill.transparency !== undefined && params.fill.transparency < 1) {
                            console.log(`[CreateOrEditShapeTool:applyStyling] Setting transparency: ${params.fill.transparency}`);
                            shape.fill.transparency = params.fill.transparency;
                        }
                    }
                } else if (params.fill.type === 'image') {
                    // Office.js doesn't support image fills directly
                    // Throw to trigger COM fallback which handles image fills
                    throw new Error('IMAGE_FILL_REQUIRES_COM');
                }
                console.log(`[CreateOrEditShapeTool:applyStyling] Fill applied successfully`);
            } catch (fillError) {
                // Re-throw image fill errors to trigger COM fallback
                if (fillError instanceof Error && fillError.message === 'IMAGE_FILL_REQUIRES_COM') {
                    throw fillError;
                }
                console.warn(`[CreateOrEditShapeTool:applyStyling] Fill failed:`, fillError);
                warnings.push(`Could not apply fill: ${fillError}`);
            }
        } else if (params.shapeType === 'textbox' && params.operationType === 'create') {
            // Default new textbox to no fill (leave existing fills unchanged on edit)
            console.log(`[CreateOrEditShapeTool:applyStyling] New textbox with no fill specified - clearing fill`);
            try {
                shape.fill.clear();
            } catch {
                // Ignore fill clear errors
            }
        }
        
        // Apply outline
        if (params.outline) {
            console.log(`[CreateOrEditShapeTool:applyStyling] Applying outline:`, params.outline);
            try {
                if (params.outline.null || params.shapeType === 'textbox') {
                    // No outline - use lineFormat.visible = false (per MS Learn docs)
                    // Setting weight = 0 doesn't hide the outline, it just makes it thin
                    shape.lineFormat.visible = false;
                } else {
                    // Ensure outline is visible when applying color/weight
                    shape.lineFormat.visible = true;
                    if (params.outline.color) {
                        shape.lineFormat.color = params.outline.color;
                    }
                    if (params.outline.weight !== undefined) {
                        shape.lineFormat.weight = params.outline.weight;
                    }
                }
                console.log(`[CreateOrEditShapeTool:applyStyling] Outline applied successfully`);
            } catch (outlineError) {
                console.warn(`[CreateOrEditShapeTool:applyStyling] Outline failed:`, outlineError);
                warnings.push(`Could not apply outline: ${outlineError}`);
            }
        } else if (params.shapeType === 'textbox' && params.operationType === 'create') {
            // Default new textbox to no outline (leave existing outlines unchanged on edit)
            try {
                shape.lineFormat.visible = false;
            } catch {
                // Ignore outline clear errors
            }
        }
        
        // Apply rotation (Note: Office.js has limited rotation support)
        if (params.rotation !== undefined) {
            // PowerPoint Office.js doesn't expose shape.rotation directly
            // Rotation requires COM fallback for full support
            warnings.push(`Shape rotation (${params.rotation}°) requires COM - Office.js has limited rotation support`);
        }
        
        // Apply text content
        if (params.textContent?.paragraphOperations) {
            console.log(`[CreateOrEditShapeTool:applyStyling] Applying text content with ${params.textContent.paragraphOperations.length} operations...`);
            try {
                await this.applyTextFrameOfficeJS(shape, params, context, warnings);
                await this.applyTextContentOfficeJS(shape, params, warnings);
                console.log(`[CreateOrEditShapeTool:applyStyling] Text content applied successfully`);
            } catch (textError) {
                console.error(`[CreateOrEditShapeTool:applyStyling] Text content failed:`, textError);
                warnings.push(`Could not apply text content: ${textError}`);
            }
        }
        
        console.log(`[CreateOrEditShapeTool:applyStyling] Final sync...`);
        try {
            await context.sync();
            console.log(`[CreateOrEditShapeTool:applyStyling] Final sync SUCCESS - Styling complete`);
        } catch (syncError) {
            console.error(`[CreateOrEditShapeTool:applyStyling] Final sync FAILED:`, syncError);
            console.error(`[CreateOrEditShapeTool:applyStyling] Error details:`, {
                message: syncError instanceof Error ? syncError.message : String(syncError),
                stack: syncError instanceof Error ? syncError.stack : undefined,
            });
            throw syncError;  // Re-throw to trigger COM fallback
        }
    }

    /**
     * Apply text content to a shape using paragraph operations
     */
    private async applyTextContentOfficeJS(
        shape: PowerPoint.Shape,
        params: CreateOrEditShapeParams,
        warnings: string[]
    ): Promise<void> {
        console.log(`[CreateOrEditShapeTool:applyText] Starting text content application...`);
        const textFrame = shape.textFrame;

        const operations = params.textContent?.paragraphOperations || [];
        console.log(`[CreateOrEditShapeTool:applyText] Processing ${operations.length} paragraph operations`);

        // Separate operations by type
        const editOps = operations.filter(op => op.operation === 'edit' && op.paragraph?.setText);
        const insertOps = operations.filter(op => op.operation === 'insert' && op.paragraph?.setText);
        const removeOps = operations.filter(op => op.operation === 'remove');
        const replaceOps = operations.filter(op => op.operation === 'edit' && op.paragraph?.replaceText);
        const formatOnlyOps = operations.filter(op => op.operation === 'edit' && !op.paragraph?.setText && !op.paragraph?.replaceText);

        // Handle replaceText operations - these need COM
        if (replaceOps.length > 0) {
            console.log(`[CreateOrEditShapeTool:applyText] ${replaceOps.length} replaceText operations - these need COM for formatting preservation`);
            warnings.push(`replaceText operations need COM for formatting preservation`);
        }

        // Handle format-only edits (no setText/replaceText, alignment-only changes)
        // These should NOT replace text content
        if (formatOnlyOps.length > 0 && editOps.length === 0 && insertOps.length === 0) {
            console.log(`[CreateOrEditShapeTool:applyText] ${formatOnlyOps.length} format-only edit operations`);
            for (const op of formatOnlyOps) {
                if (op.paragraph) {
                    // Apply formatting without changing text
                    this.applyParagraphFormatting(textFrame.textRange, op.paragraph, warnings);
                }
            }
            return; // Don't process further - we only wanted to change formatting
        }

        // Combine edit + insert operations by index to build final paragraph list
        const setTextOps = [...editOps, ...insertOps].filter(op => op.paragraph?.setText);

        if (setTextOps.length > 0) {
            // Sort by index and collect texts
            const sortedOps = [...setTextOps].sort((a, b) => a.index - b.index);
            const paragraphTexts: string[] = [];

            for (const op of sortedOps) {
                if (op.paragraph?.setText) {
                    paragraphTexts[op.index] = op.paragraph.setText;
                }
            }

            // Filter out undefined entries and join with newlines
            const combinedText = paragraphTexts.filter(t => t !== undefined).join('\n');
            console.log(`[CreateOrEditShapeTool:applyText] Combined ${setTextOps.length} operations into ${paragraphTexts.filter(t => t).length} paragraphs`);

            try {
                textFrame.textRange.text = combinedText;

                // Apply paragraph-level formatting from the first operation
                const firstOp = sortedOps[0];
                if (firstOp?.paragraph) {
                    this.applyParagraphFormatting(textFrame.textRange, firstOp.paragraph, warnings);
                }
                console.log(`[CreateOrEditShapeTool:applyText] Text operations applied successfully`);
            } catch (textError) {
                console.error(`[CreateOrEditShapeTool:applyText] Text operations failed:`, textError);
                warnings.push(`Text operations failed: ${textError}`);
            }
        }

        // Handle remove operations (log warning - complex to implement properly)
        if (removeOps.length > 0) {
            console.log(`[CreateOrEditShapeTool:applyText] ${removeOps.length} remove operations - not fully implemented`);
            warnings.push(`Remove operations are not fully supported in Office.js text editing`);
        }
    }

    private async applyTextFrameOfficeJS(
        shape: PowerPoint.Shape,
        params: CreateOrEditShapeParams,
        context: PowerPoint.RequestContext,
        warnings: string[]
    ): Promise<void> {
        const textFrame = shape.textFrame as unknown as {
            leftMargin?: number;
            rightMargin?: number;
            topMargin?: number;
            bottomMargin?: number;
            autoSize?: PowerPoint.ShapeAutoSize;
        };

        if (!textFrame) {
            return;
        }

        // Default autofit to shrink text on overflow unless explicitly set
        try {
            const autofit = params.textFrame?.autofit ?? "shrink_on_overflow";
            const autofitMap = {
                "none": PowerPoint.ShapeAutoSize.autoSizeNone,
                "shrink_on_overflow": PowerPoint.ShapeAutoSize.autoSizeTextToFitShape,
                "resize_shape": PowerPoint.ShapeAutoSize.autoSizeShapeToFitText,
            };
            if (autofit in autofitMap) {
                textFrame.autoSize = autofitMap[autofit as keyof typeof autofitMap];
                console.log(`[CreateOrEditShapeTool:applyStyling] Set autofit to: ${autofit}`);
            }
        } catch (autofitError) {
            console.warn(`[CreateOrEditShapeTool:applyStyling] Could not set autofit:`, autofitError);
            warnings.push(`Could not set autofit: ${autofitError}`);
        }

        let width = params.position?.width;
        let height = params.position?.height;

        if (width === undefined || height === undefined) {
            shape.load(['width', 'height']);
            await context.sync();
            width = shape.width;
            height = shape.height;
        }

        if (width === undefined || height === undefined) {
            warnings.push('Could not determine shape size to apply text margins');
            return;
        }

        const percentToPoints = (percent: number | undefined, total: number) =>
            percent === undefined ? undefined : (percent / 100) * total;

        const resolveMargin = (
            pointValue: number | undefined,
            percentValue: number | undefined,
            total: number,
            defaultPercent: number
        ) => {
            if (percentValue !== undefined) {
                return percentToPoints(percentValue, total);
            }
            if (pointValue !== undefined) {
                return pointValue;
            }
            return percentToPoints(defaultPercent, total);
        };

        const tf = params.textFrame;
        const left = resolveMargin(tf?.leftMargin, (tf as any)?.leftMarginPercent, width, DEFAULT_TEXT_MARGIN_PERCENT_HORIZONTAL);
        const right = resolveMargin(tf?.rightMargin, (tf as any)?.rightMarginPercent, width, DEFAULT_TEXT_MARGIN_PERCENT_HORIZONTAL);
        const top = resolveMargin(tf?.topMargin, (tf as any)?.topMarginPercent, height, DEFAULT_TEXT_MARGIN_PERCENT_VERTICAL);
        const bottom = resolveMargin(tf?.bottomMargin, (tf as any)?.bottomMarginPercent, height, DEFAULT_TEXT_MARGIN_PERCENT_VERTICAL);

        try {
            if (left !== undefined) textFrame.leftMargin = left;
            if (right !== undefined) textFrame.rightMargin = right;
            if (top !== undefined) textFrame.topMargin = top;
            if (bottom !== undefined) textFrame.bottomMargin = bottom;
        } catch (err) {
            warnings.push(`Could not apply text frame margins: ${err}`);
        }
    }

    /**
     * Apply formatting to a text range
     */
    private applyParagraphFormatting(
        textRange: PowerPoint.TextRange,
        paragraph: {
            paragraphAlignment?: string;
        },
        warnings: string[]
    ): void {
        try {
            if (paragraph.paragraphAlignment) {
                const alignmentMap: Record<string, PowerPoint.ParagraphHorizontalAlignment> = {
                    'left': PowerPoint.ParagraphHorizontalAlignment.left,
                    'center': PowerPoint.ParagraphHorizontalAlignment.center,
                    'right': PowerPoint.ParagraphHorizontalAlignment.right,
                    'justify': PowerPoint.ParagraphHorizontalAlignment.justify,
                    'distributed': PowerPoint.ParagraphHorizontalAlignment.distributed,
                };
                const alignment = alignmentMap[paragraph.paragraphAlignment];
                if (alignment !== undefined) {
                    textRange.paragraphFormat.horizontalAlignment = alignment;
                }
            }
        } catch (e) {
            warnings.push(`Could not apply some formatting: ${e}`);
        }
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // COM IMPLEMENTATION (FALLBACK)
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Execute shape creation using Python COM backend (fallback)
     */
    private async executeWithCOM(params: CreateOrEditShapeParams): Promise<CreateOrEditShapeResult> {
        const warnings: string[] = [];
        const errors: string[] = [];
        
        // Get deck ID for COM connection
        const documentId = await getOrCreatedocumentId();
        console.log(`[CreateOrEditShapeTool] COM fallback using documentId: ${documentId}`);

        // Resolve image fill if present
        let imageBase64: string | undefined;
        let imageMimeType: string | undefined;
        let imageFillMode: string | undefined;
        let aspectRatio: number | undefined;
        
        if (params.fill?.type === 'image' && params.fill.imageSource) {
            const contextResolver = getContextResolver();
            const imageData = contextResolver.getImage(params.fill.imageSource);
            
            if (imageData) {
                imageBase64 = imageData.base64;
                imageMimeType = imageData.mimeType;
                imageFillMode = params.fill.fillMode ?? 'stretch';
                console.log(`[CreateOrEditShapeTool] ✅ Resolved image asset: ${params.fill.imageSource} (${imageMimeType})`);
                
                // Extract aspect ratio from image (calculates visual dimensions if needed)
                // Preserve aspect ratio for all fill modes except 'stretch'
                // 'stretch' mode should fill the entire shape regardless of aspect ratio
                if (imageFillMode !== 'stretch') {
                    aspectRatio = await getAspectRatioFromImage(imageBase64, imageData.metadata);
                    console.log(`[CreateOrEditShapeTool] Aspect ratio: ${aspectRatio.toFixed(2)} for fill mode: ${imageFillMode}`);
                } else if (imageFillMode === 'stretch') {
                    console.log(`[CreateOrEditShapeTool] Fill mode is 'stretch' - aspect ratio will not be preserved`);
                }
            } else {
                warnings.push(`Image asset not found in cache: ${params.fill.imageSource}`);
            }
        }

        // Extract text and formatting from paragraph operations
        console.log(`[CreateOrEditShapeTool:COM] Extracting text data from params...`);
        console.log(`[CreateOrEditShapeTool:COM] params.textContent:`, JSON.stringify(params.textContent, null, 2));
        const textData = this.extractTextDataFromParams(params);
        console.log(`[CreateOrEditShapeTool:COM] Extracted textData:`, JSON.stringify(textData, null, 2));

        // Extract fill properties
        const fillColor = params.fill?.type === 'solid' ? params.fill.color : undefined;
        const fillNull = params.fill?.type === 'solid' ? params.fill.null : undefined;
        const fillTransparency = params.fill?.type === 'solid' ? params.fill.transparency : undefined;
        
        console.log(`[CreateOrEditShapeTool:COM] Fill extraction: color=${fillColor}, null=${fillNull}, transparency=${fillTransparency}`);

        // Resolve position with aspect ratio preservation for image fills
        let resolvedPosition: { left: number; top: number; width: number; height: number } | undefined;
        if (params.position || params.operationType === "create") {
            const positionInput = {
                left: params.position?.left ?? 100,
                top: params.position?.top ?? 100,
                width: params.position?.width,
                height: params.position?.height,
            };
            
            // If image fill with non-stretch mode, preserve aspect ratio
            // This ensures the shape dimensions match the image's aspect ratio
            if (params.fill?.type === 'image' && imageFillMode !== 'stretch' && aspectRatio) {
                resolvedPosition = resolveImagePosition(positionInput, aspectRatio);
                console.log(`[CreateOrEditShapeTool] Resolved position with aspect ratio ${aspectRatio}:`, {
                    original: positionInput,
                    resolved: resolvedPosition
                });
            } else {
                // Use provided dimensions or defaults
                resolvedPosition = {
                    left: positionInput.left,
                    top: positionInput.top,
                    width: positionInput.width ?? 200,
                    height: positionInput.height ?? 100,
                };
            }
        }

        // Build request payload for Python COM backend
        const comPayload = {
            documentId,
            operationType: params.operationType,
            slideId: params.slideId,
            shapeType: params.shapeType ?? 'rectangle',
            shapeId: params.shapeId ?? 0,
            position: resolvedPosition,
            rotation: params.rotation ?? 0,
            // Extract text from paragraph operations if present
            text: textData?.text,
            // Text formatting for COM backend
            textFormatting: textData?.alignment ? { alignment: textData.alignment } : undefined,
            textFormat: textData?.runs,
            fillColor,
            fillNull,
            fillTransparency,
            outlineColor: params.outline?.color,
            outlineNull: params.outline?.null,
            // Image fill data (if present)
            imageBase64,
            imageMimeType,
            imageFillMode,
        };

        console.log(`[CreateOrEditShapeTool:COM] ========== COM PAYLOAD ==========`);
        console.log(`[CreateOrEditShapeTool:COM] Full payload:`, JSON.stringify(comPayload, null, 2));
        console.log(`[CreateOrEditShapeTool:COM] textFormatting specifically:`, JSON.stringify(comPayload.textFormatting, null, 2));
        console.log(`[CreateOrEditShapeTool:COM] ================================`);

        // Call Python COM backend through backendClient
        const comResult = await backendClient.comCreateShape(comPayload);
        console.log(`[CreateOrEditShapeTool] COM backend response:`, comResult);

        if (!comResult.success) {
            errors.push(comResult.message || 'COM operation failed');
        }

        // Add error message to errors if operation failed
        if (!comResult.success && comResult.message) {
            errors.push(comResult.message);
        }

        return {
            success: comResult.success,
            slideId: params.slideId,
            shapeId: comResult.shapeId ?? params.shapeId ?? 0,
            operationType: params.operationType,
            position: roundPosition(comResult.position ?? resolvedPosition),
            warnings,
            errors,
            executionMethod: "com" as const,
        };
    }

    /**
     * Execute replaceText operations via COM backend.
     * Uses PowerPoint's native TextRange.Replace() which PRESERVES ALL FORMATTING.
     * This is essential for clone+edit workflows where users want to keep original styling.
     */
    private async executeReplaceTextWithCOM(
        params: CreateOrEditShapeParams,
        replaceTextOps: Array<{ paragraph?: { replaceText?: { find: string; replace: string } } }>
    ): Promise<CreateOrEditShapeResult> {
        const warnings: string[] = [];
        const errors: string[] = [];

        // Get deck ID for COM connection
        const documentId = await getOrCreatedocumentId();

        // Build replacements array from operations
        const replacements = replaceTextOps
            .filter(op => op.paragraph?.replaceText)
            .map(op => ({
                find: op.paragraph!.replaceText!.find,
                replace: op.paragraph!.replaceText!.replace,
                matchCase: false,
            }));

        if (replacements.length === 0) {
            return {
                success: false,
                slideId: params.slideId,
                shapeId: params.shapeId ?? 0,
                operationType: params.operationType,
                warnings: [],
                errors: ['No valid replaceText operations found'],
            };
        }

        // Call COM backend
        const comResult = await backendClient.comReplaceText({
            documentId,
            slideId: params.slideId,
            shapes: [{
                shapeId: params.shapeId!,
                replacements,
            }],
        });

        if (!comResult.success) {
            errors.push(comResult.error || 'COM replaceText failed');
        }

        // Collect warnings from results
        if (comResult.results) {
            for (const result of comResult.results) {
                warnings.push(...result.warnings);
            }
        }

        return {
            success: comResult.success,
            slideId: params.slideId,
            shapeId: params.shapeId ?? 0,
            operationType: params.operationType,
            warnings,
            errors,
            executionMethod: "com" as const,
        };
    }

    /**
     * Extract text and formatting from paragraph operations for COM backend
     */
    private extractTextDataFromParams(params: CreateOrEditShapeParams): {
        text: string;
        runs?: Array<{
            text: string;
            fontFamily?: string;
            fontSize?: number;
            bold?: boolean;
            italic?: boolean;
            underline?: boolean;
            strikethrough?: boolean;
            textColor?: string;
            highlight?: string;
            superscript?: boolean;
            subscript?: boolean;
        }>;
        alignment?: string;
    } | undefined {
        console.log(`[CreateOrEditShapeTool:extractTextData] Called with params.textContent:`, params.textContent);
        
        if (!params.textContent?.paragraphOperations) {
            console.log(`[CreateOrEditShapeTool:extractTextData] No paragraphOperations found, returning undefined`);
            return undefined;
        }

        console.log(`[CreateOrEditShapeTool:extractTextData] Found ${params.textContent.paragraphOperations.length} paragraph operations`);

        // Collect all setText values from insert operations
        const textParts: string[] = [];
        const runs: Array<{
            text: string;
            fontFamily?: string;
            fontSize?: number;
            bold?: boolean;
            italic?: boolean;
            underline?: boolean;
            strikethrough?: boolean;
            textColor?: string;
            highlight?: string;
            superscript?: boolean;
            subscript?: boolean;
        }> = [];
        let alignment: string | undefined;
        
        for (let i = 0; i < params.textContent.paragraphOperations.length; i++) {
            const op = params.textContent.paragraphOperations[i];
            console.log(`[CreateOrEditShapeTool:extractTextData] Processing op[${i}]:`, JSON.stringify(op, null, 2));
            
            if ((op.operation === 'insert' || op.operation === 'edit') && op.paragraph?.setText) {
                textParts.push(op.paragraph.setText);
                console.log(`[CreateOrEditShapeTool:extractTextData] Added text: "${op.paragraph.setText}"`);
                
                if (textParts.length === 1 && op.paragraph?.paragraphAlignment) {
                    alignment = op.paragraph.paragraphAlignment;
                }
                if (op.paragraph?.textFormat?.length) {
                    runs.push(...op.paragraph.textFormat);
                }
            } else {
                console.log(`[CreateOrEditShapeTool:extractTextData] Skipping op (operation=${op.operation}, hasSetText=${!!op.paragraph?.setText})`);
            }
        }

        if (textParts.length === 0) {
            console.log(`[CreateOrEditShapeTool:extractTextData] No text parts found, returning undefined`);
            return undefined;
        }

        const result = {
            text: textParts.join('\n'),
            runs: runs.length > 0 ? runs : undefined,
            alignment,
        };
        console.log(`[CreateOrEditShapeTool:extractTextData] Final result:`, JSON.stringify(result, null, 2));
        return result;
    }
}

